import { ensureProtocol } from '@libertify.frontend/adapters';
import { useAnalytics } from '../hooks/useAnalytics';
import {
  CallToActions,
  MediaAnalyticsEvents,
  MediaProjectInstanceResponse,
} from '../types';
import {
  DownloadDocumentAction,
  EmailAction,
  ExternalLinkAction,
  PhoneAction,
  TakeAppointmentAction,
} from './CallToAction';

export const CallToActionList = ({
  callToActions,
  publication,
  locale = 'en-US', // todo in all parent code
}: {
  callToActions: CallToActions;
  publication: MediaProjectInstanceResponse;
  locale: string;
}) => {
  const { trackEvent } = useAnalytics();

  const getLabel = (labels: Record<string, string>) => {
    // some values could be sent as ''  from admin app
    const nonEmptyValues = Object.values(labels).filter(Boolean);
    // if only 1 language was defined in CTAs, use it
    // the current user browser might accept another language than this one
    const uniqueFallback = nonEmptyValues.length === 1 ? nonEmptyValues[0] : '';
    return labels[locale] || labels['en-US'] || uniqueFallback || '';
  };

  return callToActions?.actions?.map((callToAction) => {
    const label = getLabel(callToAction.ui_definition.labels);
    const handleClick = () => {
      const actionType = callToAction.action.type;

      let url: string | undefined;
      if (actionType === 'DocumentDownload') {
        url = publication.documents?.Document_0?.path;
      } else if (callToAction.action.url) {
        const protocolHint =
          actionType === 'Email' || actionType === 'Phone'
            ? actionType
            : undefined;
        url = ensureProtocol(callToAction.action.url, protocolHint);
      }

      trackEvent(MediaAnalyticsEvents.CtaClick, {
        cta_type: actionType,
        cta_label: label,
      });

      if (!url) return;
      const target = actionType === 'Email' ? '_self' : '_blank';
      window.open(url, target);
    };

    if (callToAction.action.type === 'ExternalLink') {
      return (
        <ExternalLinkAction
          key={callToAction.id}
          label={label}
          onClick={handleClick}
        />
      );
    } else if (callToAction.action.type === 'DocumentDownload') {
      return (
        <DownloadDocumentAction
          key={callToAction.id}
          label={label}
          onClick={handleClick}
          publication={publication}
        />
      );
    } else if (callToAction.action.type === 'Email') {
      return (
        <EmailAction
          key={callToAction.id}
          label={label}
          onClick={handleClick}
          publication={publication}
        />
      );
    } else if (callToAction.action.type === 'Phone') {
      return (
        <PhoneAction
          key={callToAction.id}
          label={label}
          onClick={handleClick}
        />
      );
    } else if (callToAction.action.type === 'CalendarAppointment') {
      return (
        <TakeAppointmentAction
          key={callToAction.id}
          label={label}
          onClick={handleClick}
        />
      );
    }
    return null;
  });
};
